import { Box, forwardRef, StyleProps, useColorModeValue } from '@chakra-ui/react';
import { PropsWithChildren } from 'react';

import { createUseVariantStyle } from '@bonusly/ds-hooks';
import { rem } from '@bonusly/ds-utils';

import { useNameStyle } from '../hooks/use-name-style';
import { AvatarContextProvider } from '../lib/context';
import { AvatarSize, AvatarVariant } from '../types';
import { AvatarImage } from './avatar-image';
import { AvatarImageWrapper } from './avatar-image-wrapper';

export interface AvatarProps extends StyleProps, PropsWithChildren {
	name?: string;
	size?: AvatarSize;
	src?: string;
	variant?: AvatarVariant;
}

export const AvatarSizeMap: Record<AvatarSize, string> = {
	sm: rem(32),
	md: rem(40),
	lg: rem(48),
	xl: rem(56),
	'2xl': rem(72),
};

const useVariantStyle = createUseVariantStyle({
	size: {
		sm: {
			boxSize: AvatarSizeMap.sm,
			fontSize: 'sm',
		},
		md: {
			boxSize: AvatarSizeMap.md,
			fontSize: 'md',
		},
		lg: {
			boxSize: AvatarSizeMap.lg,
			fontSize: 'md',
		},
		xl: {
			boxSize: AvatarSizeMap.xl,
			fontSize: 'lg',
		},
		'2xl': {
			boxSize: AvatarSizeMap['2xl'],
			fontSize: 'xl',
		},
	},
	variant: {
		solid: {},
		outline: {},
	},
});

const Avatar = forwardRef(({ name, size = 'md', src, variant = 'solid', ...rest }: AvatarProps, ref) => {
	const style = useVariantStyle({ size, variant });
	const { bg, color: textColor } = useNameStyle(name);
	const outlineColor = useColorModeValue('white', 'bg.canvas');

	return (
		<AvatarContextProvider value={{ bg, size, textColor, variant }}>
			<Box
				alignItems="center"
				as="span"
				bg={variant === 'solid' ? bg : 'white'}
				borderRadius="full"
				overflow="hidden"
				display="inline-flex"
				flexShrink={0}
				justifyContent="center"
				outline="1px solid"
				outlineColor={outlineColor}
				data-testid={`avatar-${name}`}
				ref={ref}
				{...style}
				{...rest}
			>
				<AvatarImageWrapper>
					<AvatarImage name={name} src={src} />
				</AvatarImageWrapper>
			</Box>
		</AvatarContextProvider>
	);
});

Avatar.displayName = 'Avatar';

export { Avatar };
